import { Component, Input, Output, EventEmitter, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';

export interface MenubarItem {
  label: string;
  icon?: string;
  items?: MenubarItem[];
  command?: () => void;
  routerLink?: string | string[];
  queryParams?: Record<string, unknown>;
  disabled?: boolean;
  visible?: boolean;
  separator?: boolean;
  styleClass?: string;
}

/**
 * CfiMenubarComponent - Material Design menubar replacement for PrimeNG Menubar
 *
 * Migration from PrimeNG:
 * - Replace `<p-menubar [model]="items"></p-menubar>`
 * - With `<cfi-menubar [items]="items"></cfi-menubar>`
 * - MenuItem interface maps to MenubarItem with same properties
 *
 * @example
 * ```typescript
 * items: MenubarItem[] = [
 *   {
 *     label: 'File',
 *     icon: 'folder',
 *     items: [
 *       { label: 'New', icon: 'add', command: () => this.create() },
 *       { label: 'Open', icon: 'folder_open', routerLink: '/open' }
 *     ]
 *   }
 * ];
 * ```
 */
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'cfi-menubar',
  templateUrl: './menubar.component.html',
  styleUrls: ['./menubar.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatDividerModule
  ],
  standalone: true
})
export class CfiMenubarComponent {
  @Input() items: MenubarItem[] = [];
  @Input() styleClass?: string;
  @Output() itemClick = new EventEmitter<MenubarItem>();

  private readonly router = inject(Router);

  /**
   * Handle menu item click
   */
  onItemClick(item: MenubarItem, event?: Event): void {
    if (item.disabled) {
      event?.preventDefault();
      return;
    }

    // Execute command if provided
    if (item.command) {
      item.command();
    }

    // Navigate if routerLink is provided
    if (item.routerLink) {
      const link = Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];
      this.router.navigate(link, { queryParams: item.queryParams });
    }

    // Emit click event
    this.itemClick.emit(item);

    event?.stopPropagation();
  }

  /**
   * Check if item should be displayed
   */
  isVisible(item: MenubarItem): boolean {
    return item.visible !== false;
  }

  /**
   * Check if item has submenu
   */
  hasSubmenu(item: MenubarItem): boolean {
    return !!item.items && item.items.length > 0;
  }

  /**
   * Track by function for ngFor
   */
  trackByLabel(index: number, item: MenubarItem): string {
    return item.label + index;
  }
}
